import { useState, useEffect, useCallback } from "react";
import { Calendar, FileText, IndianRupee, Loader2 } from "lucide-react";
import { ContractorApi } from "api/contractors/service";

// Helper components
const Card = ({ className = "", children }) => (
  <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ className = "", children }) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ className = "", children }) => (
  <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 pt-0 ${className}`}>{children}</div>
);

export function ContractorDashboardTab({
  contractor,
  workOrders,
  payments,
}) {
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAttendance = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await ContractorApi.getAttendanceByContractor(contractor.id);
      if (response.status) {
        setAttendanceLogs(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching attendance for dashboard:", err);
    } finally {
      setIsLoading(false);
    }
  }, [contractor.id]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Generate calendar data for attendance
  const generateCalendarData = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const calendarDays = [];

    for (let i = 1; i <= daysInMonth; i++) {
      // Pad month and day with leading zeros
      const dayDate = new Date(currentYear, currentMonth, i);
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;

      // Get attendance records for this specific date
      const attendanceForDay = attendanceLogs.filter(log => log.date === dateStr);
      const totalWorkers = attendanceForDay.reduce((sum, log) => sum + (log.person_count || 0), 0);
      const workOrderIds = attendanceForDay.map(log => log.work_order_id || log.work_order);

      calendarDays.push({
        date: i,
        workers: totalWorkers,
        workOrders: workOrderIds,
        isToday: i === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear(),
      });
    }

    return { calendarDays, firstDayOfMonth, currentMonth, currentYear };
  };

  const { calendarDays, firstDayOfMonth, currentMonth, currentYear } = generateCalendarData();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const totalDaysWorked = attendanceLogs.length > 0
    ? new Set(attendanceLogs.map(log => log.date)).size
    : 0;
  const totalManpower = attendanceLogs.reduce((sum, log) => sum + (log.person_count || 0), 0);
  const activeWorkOrdersCount = workOrders.filter(wo => wo.status === "Active").length;

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  // Recent activity
  const recentActivity = [
    ...payments.slice(0, 3).map(p => ({
      type: "payment",
      description: `Payment of ${formatCurrency(p.amount)} recorded for ${p.projectName} by ${p.created_by || p.createdBy || "Unknown"}`,
      date: p.date,
    })),
    ...workOrders.filter(wo => wo.status === "Active").slice(0, 2).map(wo => ({
      type: "workorder",
      description: `Work Order ${wo.id} is active for ${wo.projectName}`,
      date: wo.startDate,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Calendar */}
      <Card className="col-span-2 border-border/60 shadow-none">
        <CardHeader className="pb-3 pt-4 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4 text-sm-secondary" />
            Attendance Calendar - {monthNames[currentMonth]} {currentYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-xs text-center text-muted-foreground font-medium py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 min-h-[240px] relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-20 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-sm-primary" />
              </div>
            )}

            {/* Empty cells for days before the 1st */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="h-12" />
            ))}

            {/* Calendar days */}
            {calendarDays.map((day) => (
              <div
                key={day.date}
                className={`h-12 rounded-lg p-1 text-center relative group transition-colors ${day.isToday
                  ? "bg-sm-primary/10 border border-primary/30"
                  : day.workers > 0
                    ? "bg-sm-secondary/5 hover:bg-sm-secondary/10"
                    : "hover:bg-muted/50"
                  }`}
              >
                <span className={`text-xs ${day.isToday ? "font-semibold text-sm-primary" : "text-foreground"}`}>
                  {day.date}
                </span>
                {day.workers > 0 && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                    <span className="text-[10px] font-medium text-sm-secondary">{day.workers}</span>
                  </div>
                )}

                {/* Tooltip */}
                {day.workers > 0 && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-30 w-max pointer-events-none">
                    <div className="bg-white dark:bg-zinc-950 border border-border rounded-lg shadow-xl p-2 text-left min-w-[140px]">
                      <p className="text-xs font-semibold text-foreground">{day.workers} workers</p>
                      <div className="mt-1 space-y-0.5">
                        {day.workOrders.map((wo, i) => (
                          <p key={i} className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <div className="h-1 w-1 rounded-full bg-sm-secondary" />
                            WO: #{wo}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Stats below calendar */}
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border/60">
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{totalDaysWorked}</p>
              <p className="text-xs text-muted-foreground">Days Worked</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{totalManpower}</p>
              <p className="text-xs text-muted-foreground">Total Manpower</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{activeWorkOrdersCount}</p>
              <p className="text-xs text-muted-foreground">Active Work Orders</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Feed */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3 pt-4 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-sm-secondary" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start gap-3 pb-3 border-b border-border/40 last:border-0 last:pb-0">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${activity.type === "payment" ? "bg-green-500/10" : "bg-blue-500/10"
                  }`}>
                  {activity.type === "payment" ? (
                    <IndianRupee className="h-4 w-4 text-green-600" />
                  ) : (
                    <FileText className="h-4 w-4 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground leading-relaxed">{activity.description}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(activity.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
              </div>
            ))}

            {recentActivity.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

